"use client";

import { useEffect, useId, useState, type FormEvent, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const SIGN_IN_CODE_LENGTH = 6;

interface CodeEntryFormProps {
  initialCode?: string;
  codeLabel?: string;
  intro?: ReactNode;
  isVerifying: boolean;
  error: string | null;
  submitLabel: string;
  busyLabel: string;
  onVerify: (digits: string) => Promise<void>;
  onResend?: () => Promise<boolean>;
  resendAvailableAt?: number;
  onChangeEmail: () => void;
  changeEmailLabel?: string;
  onEdit: () => void;
  autoSubmit?: boolean;
}

export function CodeEntryForm(props: CodeEntryFormProps) {
  const [code, setCode] = useState(props.initialCode ?? "");
  const [localError, setLocalError] = useState<string | null>(null);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const digits = code.replace(/\D/g, "");
    if (digits.length !== SIGN_IN_CODE_LENGTH) {
      setLocalError(`Enter the ${SIGN_IN_CODE_LENGTH}-digit code.`);
      return;
    }
    setLocalError(null);
    await props.onVerify(digits);
  };

  const change = (value: string) => {
    setCode(value);
    setLocalError(null);
    props.onEdit();
    const digits = value.replace(/\D/g, "");
    if (props.autoSubmit && digits.length === SIGN_IN_CODE_LENGTH && !props.isVerifying) void props.onVerify(digits);
  };

  const error = localError ?? props.error;
  return (
    <form className="space-y-3" onSubmit={submit}>
      {props.intro}
      <CodeInput
        label={props.codeLabel ?? "Sign-in code"}
        value={code}
        onChange={change}
      />
      {error ? (
        <p role="alert" className="text-sm text-destructive-strong">
          {error}
        </p>
      ) : null}
      <Button type="submit" variant="primary" className="w-full" disabled={props.isVerifying}>
        {props.isVerifying ? props.busyLabel : props.submitLabel}
      </Button>
      <CodeEntryLinks
        onResend={props.onResend}
        resendAvailableAt={props.resendAvailableAt}
        onChangeEmail={props.onChangeEmail}
        changeEmailLabel={props.changeEmailLabel ?? "Use a different email"}
      />
    </form>
  );
}

function CodeInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const inputId = useId();
  return (
    <div className="space-y-1">
      <Label htmlFor={inputId}>{label}</Label>
      <Input
        id={inputId}
        variant="mono"
        inputMode="numeric"
        autoComplete="one-time-code"
        autoFocus
        required
        maxLength={SIGN_IN_CODE_LENGTH + 1}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );
}

function useSecondsUntil(availableAt: number | undefined): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (availableAt === undefined) return;
    const tick = () => {
      setNow(Date.now());
      if (Date.now() >= availableAt) clearInterval(timer);
    };
    const first = setTimeout(tick, 0);
    const timer = setInterval(tick, 1000);
    return () => {
      clearTimeout(first);
      clearInterval(timer);
    };
  }, [availableAt]);
  return availableAt === undefined ? 0 : Math.max(Math.ceil((availableAt - now) / 1000), 0);
}

function resendLabel(isResending: boolean, waitSeconds: number, resent: boolean): string {
  if (isResending) return "Sending…";
  if (waitSeconds > 0) return `Resend code in ${waitSeconds}s`;
  return resent ? "Code resent" : "Resend code";
}

function ResendCodeButton({
  onResend,
  availableAt,
}: {
  onResend: () => Promise<boolean>;
  availableAt?: number;
}) {
  const [resent, setResent] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const waitSeconds = useSecondsUntil(availableAt);
  const resend = async () => {
    if (isResending || waitSeconds > 0) return;
    setIsResending(true);
    try {
      setResent(await onResend());
    } finally {
      setIsResending(false);
    }
  };
  return (
    <button
      type="button"
      className="min-h-11 underline disabled:opacity-60"
      disabled={isResending || waitSeconds > 0}
      onClick={resend}
    >
      {resendLabel(isResending, waitSeconds, resent)}
    </button>
  );
}

function CodeEntryLinks({
  onResend,
  resendAvailableAt,
  onChangeEmail,
  changeEmailLabel,
}: {
  onResend?: () => Promise<boolean>;
  resendAvailableAt?: number;
  onChangeEmail: () => void;
  changeEmailLabel: string;
}) {
  return (
    <div className="flex items-center justify-between text-sm text-foreground">
      <button type="button" className="min-h-11 underline" onClick={onChangeEmail}>
        {changeEmailLabel}
      </button>
      {onResend ? <ResendCodeButton onResend={onResend} availableAt={resendAvailableAt} /> : null}
    </div>
  );
}
